/**
 * Post-job flow controller: wires the pure machine to routing, mutations, and
 * the shared shell. The step components are pure rendering over this hook —
 * every "which step", "what does the button do", "where does focus go on a
 * validation failure" decision lives here.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Platform } from 'react-native';
import { useRouter } from 'expo-router';
import { useTranslation } from 'react-i18next';
import { useToast } from '../../../hooks/useToast';
import { useHaptics } from '../../../hooks/useHaptics';
import { useLocale } from '../../../hooks/useLocale';
import { useWorkspace } from '../../../providers/useWorkspace';
import {
  useCompanyJobs,
  useCreateEmployerJob,
  useGenerateCompanyJobDraft,
  useUpdateCompanyJob,
} from '../../../hooks/useCompanyJobs';
import type { CompanyJobWithV2 } from '../../../lib/employer-jobs-contract';
import {
  getPostJobDoneHref,
  type PostJobOrigin,
} from '../post-job-flow.submit';
import { confirmDestructive } from '../../../lib/confirm-destructive';
import { trackProductEvent } from '../../../lib/product-analytics';
import {
  POST_JOB_PROGRESS_STEPS,
  POST_JOB_STEPS,
  applyAiDraft,
  applyConfidentialPatch,
  applyDistributionChoice,
  buildCreatePayload,
  buildUpdatePayload,
  createInitialJobDraftState,
  firstInvalidStep,
  hydrateFromJob,
  isSameJobContent,
  validateStep,
  type JobDraftBasicsState,
  type JobDraftCompensationState,
  type JobDraftContentState,
  type JobDraftEligibilityState,
  type JobDraftEntryState,
  type JobDraftQuestionsState,
  type JobDraftState,
  type JobDraftWorkforceState,
  type PostJobFieldError,
  type PostJobStep,
} from './post-job-machine';

export interface UsePostJobFlowOptions {
  origin?: PostJobOrigin;
  /** Present in edit mode: hydrate this job and save changes instead of creating. */
  jobId?: string | null;
}

function createIdempotencyKey(): string {
  return `jobpost_${Date.now().toString(36)}_${Math.random()
    .toString(36)
    .slice(2, 12)}`;
}

export function usePostJobFlow({
  origin = 'jobs',
  jobId = null,
}: UsePostJobFlowOptions = {}) {
  const { t } = useTranslation('employer');
  const router = useRouter();
  const toast = useToast();
  const haptics = useHaptics();
  const { locale } = useLocale();
  const { companyId } = useWorkspace();

  const isEditMode = Boolean(jobId);

  const createMutation = useCreateEmployerJob();
  const updateMutation = useUpdateCompanyJob();
  const generateMutation = useGenerateCompanyJobDraft();
  const companyJobs = useCompanyJobs(undefined, {
    enabled: isEditMode,
    limit: 100,
  });

  const [state, setState] = useState<JobDraftState>(createInitialJobDraftState);
  const [step, setStep] = useState<PostJobStep>(isEditMode ? 'basics' : 'entry');
  const [isGenerating, setIsGenerating] = useState(false);
  const [submittingAs, setSubmittingAs] = useState<'draft' | 'live' | 'update' | null>(null);
  // Steps whose validation the user has already tripped: their errors render
  // inline and live-update as the fields are fixed (§5.5).
  const [attemptedSteps, setAttemptedSteps] = useState<Set<PostJobStep>>(
    () => new Set(),
  );
  const [focusFieldId, setFocusFieldId] = useState<string | null>(null);

  const hasSubmittedRef = useRef(false);
  const hydratedRef = useRef(false);
  /** The state as it loaded — the baseline for "untouched" and "dirty". */
  const baselineRef = useRef<JobDraftState | null>(null);
  /** Set once the employer has confirmed discarding unsaved work. */
  const confirmedDiscardRef = useRef(false);
  /** Guards against stacking dialogs when back is pressed repeatedly. */
  const discardPromptOpenRef = useRef(false);
  const [baselineVersion, setBaselineVersion] = useState(0);
  const idempotencyKeyRef = useRef(createIdempotencyKey());

  /** Resolves machine-held employer-namespace keys (default question prompts). */
  const resolveText = useCallback(
    (key: string) => t(key as never) as string,
    [t],
  );

  // ── Edit-mode hydration ────────────────────────────────────────────────────

  const editJob = useMemo(
    () =>
      isEditMode
        ? ((companyJobs.jobs.find((job) => job.id === jobId) ??
            null) as CompanyJobWithV2 | null)
        : null,
    [companyJobs.jobs, isEditMode, jobId],
  );

  useEffect(() => {
    if (!isEditMode || hydratedRef.current || !editJob) return;
    hydratedRef.current = true;
    const hydrated = hydrateFromJob(editJob);
    baselineRef.current = hydrated;
    setBaselineVersion((version) => version + 1);
    setState(hydrated);
  }, [editJob, isEditMode]);

  // The query may still be disabled while the workspace resolves, so "not
  // found" is only declared once a settled fetch actually returned data (or
  // failed) — never off an empty pre-fetch cache. Until then, edit mode keeps
  // showing the hydration state instead of flashing an empty form.
  const fetchSettled = !companyJobs.isLoading && !companyJobs.isFetching;
  const jobNotFound =
    isEditMode &&
    !editJob &&
    !hydratedRef.current &&
    fetchSettled &&
    (companyJobs.jobs.length > 0 || Boolean(companyJobs.error));
  const isHydrating = isEditMode && !hydratedRef.current && !jobNotFound;

  // ── State patch helpers ────────────────────────────────────────────────────

  const update = useCallback(
    (updater: (prev: JobDraftState) => JobDraftState) => setState(updater),
    [],
  );

  const setEntry = useCallback(
    (patch: Partial<JobDraftEntryState>) =>
      setState((prev) => ({ ...prev, entry: { ...prev.entry, ...patch } })),
    [],
  );

  const setBasics = useCallback(
    (patch: Partial<JobDraftBasicsState>) =>
      setState((prev) => ({ ...prev, basics: { ...prev.basics, ...patch } })),
    [],
  );

  const setContent = useCallback(
    (patch: Partial<JobDraftContentState>) =>
      setState((prev) => ({ ...prev, content: { ...prev.content, ...patch } })),
    [],
  );

  const setCompensation = useCallback(
    (patch: Partial<JobDraftCompensationState>) =>
      setState((prev) => ({
        ...prev,
        compensation: { ...prev.compensation, ...patch },
      })),
    [],
  );

  const setEligibility = useCallback(
    (patch: Partial<Omit<JobDraftEligibilityState, 'workforceProgramme'>>) =>
      setState((prev) => ({
        ...prev,
        eligibility: { ...prev.eligibility, ...patch },
      })),
    [],
  );

  const setWorkforce = useCallback(
    (patch: Partial<JobDraftWorkforceState>) =>
      setState((prev) => ({
        ...prev,
        eligibility: {
          ...prev.eligibility,
          workforceProgramme: {
            ...prev.eligibility.workforceProgramme,
            ...patch,
          },
        },
      })),
    [],
  );

  const setQuestions = useCallback(
    (patch: Partial<JobDraftQuestionsState>) =>
      setState((prev) => ({
        ...prev,
        questions: { ...prev.questions, ...patch },
      })),
    [],
  );

  // Both transitions keep the radio and the toggle in agreement, so they live
  // in the machine where they can be tested without the hook's I/O.
  const setDistribution = useCallback(
    (distribution: JobDraftState['distribution']) =>
      setState((prev) => applyDistributionChoice(prev, distribution)),
    [],
  );

  const setConfidential = useCallback(
    (patch: Partial<JobDraftState['confidential']>) =>
      setState((prev) => applyConfidentialPatch(prev, patch)),
    [],
  );

  // ── Baseline comparison (edit mode) ────────────────────────────────────────

  /**
   * True while the role's content block is byte-identical to what loaded.
   *
   * Legacy roles can have an empty responsibilities/qualifications list, which
   * the create-time rules reject. Untouched content skips those rules AND is
   * omitted from the PATCH, so editing the title of such a role no longer
   * requires inventing list content.
   */
  const contentUntouched = useMemo(() => {
    if (!isEditMode) return false;
    const baseline = baselineRef.current;
    if (!baseline) return false;
    return isSameJobContent(state.content, baseline.content);
    // baselineVersion re-runs this once hydration lands.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baselineVersion, isEditMode, state.content]);

  const validateOptions = useMemo(
    () => ({ contentUntouched }),
    [contentUntouched],
  );

  /**
   * Create mode has no hydrated baseline to diff against, so "dirty" is
   * instead "the employer has actually put something in". The entry pre-step is
   * where a brief is typed, dictated, or extracted from an attached PDF/Word/
   * photo — often a couple of thousand characters — so closing from there
   * throws away real work and has to prompt like any other unsaved edit.
   * Picking a mode alone (a bare "Post manually" press) still doesn't.
   */
  const hasCreateInput = useMemo(
    () =>
      state.entry.brief.trim().length > 0 ||
      state.basics.title.trim().length > 0 ||
      state.basics.department.trim().length > 0,
    [state.basics.department, state.basics.title, state.entry.brief],
  );

  /** Any unsaved edit the employer would lose by closing the wizard. */
  const isDirty = useMemo(() => {
    if (hasSubmittedRef.current) return false;
    const baseline = baselineRef.current;
    if (!baseline) return hasCreateInput;
    return JSON.stringify(state) !== JSON.stringify(baseline);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baselineVersion, hasCreateInput, state]);

  // ── Validation surface ─────────────────────────────────────────────────────

  const errors: PostJobFieldError[] = useMemo(
    () => (attemptedSteps.has(step) ? validateStep(state, step, validateOptions) : []),
    [attemptedSteps, state, step, validateOptions],
  );

  const errorFor = useCallback(
    (fieldId: string): string | null =>
      errors.find((error) => error.fieldId === fieldId)?.messageKey ?? null,
    [errors],
  );

  const clearFocusField = useCallback(() => setFocusFieldId(null), []);

  const failStep = useCallback(
    (failedStep: PostJobStep, stepErrors: PostJobFieldError[]) => {
      setAttemptedSteps((prev) => new Set(prev).add(failedStep));
      setStep(failedStep);
      setFocusFieldId(stepErrors[0]?.fieldId ?? null);
      haptics.trigger('medium');
    },
    [haptics],
  );

  // ── Steps ──────────────────────────────────────────────────────────────────

  const flowSteps: readonly PostJobStep[] = isEditMode
    ? POST_JOB_PROGRESS_STEPS
    : POST_JOB_STEPS;

  /** 0-based index into the 4 visible wizard steps; -1 on the entry pre-step. */
  const progressIndex = POST_JOB_PROGRESS_STEPS.indexOf(step);

  const goToStep = useCallback((next: PostJobStep) => {
    setStep(next);
    setFocusFieldId(null);
  }, []);

  const generateFromBrief = useCallback(async () => {
    const entryErrors = validateStep(state, 'entry');
    if (entryErrors.length > 0) {
      failStep('entry', entryErrors);
      return;
    }

    haptics.trigger('selection');
    setIsGenerating(true);
    try {
      const draft = await generateMutation.mutateAsync({
        rawInput: state.entry.brief.trim(),
        locale: locale === 'ar' ? 'ar' : 'en',
      });
      setState((prev) => applyAiDraft(prev, draft));
      setStep('basics');
    } catch (error) {
      // §7.1: generation failure returns to the brief with retry/manual open.
      toast.error(
        t('jobFlow.errors.generateFailed'),
        error instanceof Error ? error.message : undefined,
      );
    } finally {
      setIsGenerating(false);
    }
  }, [failStep, generateMutation, haptics, locale, state, t, toast]);

  const startManually = useCallback(() => {
    haptics.trigger('selection');
    setEntry({ mode: 'manual' });
    setStep('basics');
  }, [haptics, setEntry]);

  const goNext = useCallback(() => {
    if (step === 'entry') {
      if (state.entry.mode === 'ai') {
        void generateFromBrief();
      } else if (state.entry.mode === 'manual') {
        startManually();
      } else {
        failStep('entry', validateStep(state, 'entry'));
      }
      return;
    }

    const stepErrors = validateStep(state, step, validateOptions);
    if (stepErrors.length > 0) {
      failStep(step, stepErrors);
      return;
    }

    const index = flowSteps.indexOf(step);
    const next = flowSteps[index + 1];
    if (next) {
      haptics.trigger('light');
      setStep(next);
      setFocusFieldId(null);
    }
  }, [
    failStep,
    flowSteps,
    generateFromBrief,
    haptics,
    startManually,
    state,
    step,
    validateOptions,
  ]);

  /**
   * Back-handler contract shared with the create/edit routes' `beforeRemove`
   * listener: returns true when the press was consumed by an in-flow step
   * back, false when the screen may actually close.
   */
  const leave = useCallback(() => {
    // Same contract as the create/edit routes' own close handlers: a raw
    // `window.history.back()` strands anyone who deep-linked into
    // `/jobs/{id}/edit` with no history entry to pop — it walks the browser
    // out of the app entirely (observed: about:blank).
    if (router.canGoBack()) {
      router.back();
      return;
    }
    router.replace(getPostJobDoneHref(origin) as never);
  }, [origin, router]);

  const stepBack = useCallback((): boolean => {
    if (hasSubmittedRef.current) return false;
    const index = flowSteps.indexOf(step);
    if (index > 0) {
      setStep(flowSteps[index - 1]);
      setFocusFieldId(null);
      return true;
    }
    // At the flow root: unsaved work gets a confirmation before it is thrown
    // away. The caller's contract is synchronous, so consume this press and
    // re-issue the close once the employer has answered.
    if (confirmedDiscardRef.current) {
      // One-shot: it exists only to let the re-issued close through once.
      // Latching it would skip the prompt for every later back press.
      confirmedDiscardRef.current = false;
      return false;
    }
    if (!isDirty) return false;
    if (discardPromptOpenRef.current) return true;
    discardPromptOpenRef.current = true;
    void confirmDestructive({
      title: t('jobFlow.discard.title') as string,
      message: t('jobFlow.discard.body') as string,
      confirmLabel: t('jobFlow.discard.confirm') as string,
      cancelLabel: t('jobFlow.discard.keepEditing') as string,
    })
      .then((confirmed) => {
        if (!confirmed) return;
        confirmedDiscardRef.current = true;
        leave();
      })
      .finally(() => {
        discardPromptOpenRef.current = false;
      });
    return true;
  }, [flowSteps, isDirty, leave, step, t]);

  // ── Submit ─────────────────────────────────────────────────────────────────

  const finishAndLeave = useCallback(() => {
    hasSubmittedRef.current = true;
    if (Platform.OS === 'web') {
      router.replace(getPostJobDoneHref(origin) as never);
    } else {
      router.back();
    }
  }, [origin, router]);

  const submit = useCallback(
    async (saveAs: 'draft' | 'live'): Promise<boolean> => {
      if (!companyId) {
        toast.error(t('jobFlow.errors.companyRequired'));
        return false;
      }

      const invalid = firstInvalidStep(state, flowSteps, validateOptions);
      if (invalid) {
        failStep(invalid.step, invalid.errors);
        toast.error(t(invalid.errors[0].messageKey as never) as string);
        return false;
      }

      setState((prev) => ({ ...prev, lifecycleChoice: saveAs }));
      setSubmittingAs(isEditMode ? 'update' : saveAs);
      try {
        if (isEditMode && jobId) {
          await updateMutation.mutateAsync({
            jobId,
            input: buildUpdatePayload(state, resolveText, validateOptions),
          });
          toast.success(t('jobFlow.toasts.savedTitle'), t('jobFlow.toasts.savedBody'));
        } else {
          await createMutation.mutateAsync(
            buildCreatePayload(
              state,
              saveAs,
              resolveText,
              idempotencyKeyRef.current,
            ),
          );
          trackProductEvent('job_posted', { companyId, status: saveAs });
          if (saveAs === 'live') {
            toast.success(
              t('jobFlow.toasts.publishedTitle'),
              t('jobFlow.toasts.publishedBody'),
            );
          } else {
            toast.success(
              t('jobFlow.toasts.draftTitle'),
              t('jobFlow.toasts.draftBody'),
            );
          }
        }
        haptics.trigger('success');
        finishAndLeave();
        return true;
      } catch (error) {
        toast.error(
          t('jobFlow.errors.submitFailed'),
          error instanceof Error
            ? error.message
            : (t('jobFlow.errors.submitFailedHint') as string),
        );
        return false;
      } finally {
        setSubmittingAs(null);
      }
    },
    [
      companyId,
      createMutation,
      failStep,
      finishAndLeave,
      flowSteps,
      haptics,
      isEditMode,
      jobId,
      resolveText,
      state,
      t,
      toast,
      updateMutation,
      validateOptions,
    ],
  );

  const isSubmitting = submittingAs !== null;

  return {
    // mode + data
    isEditMode,
    isHydrating,
    jobNotFound,
    origin,

    // state
    state,
    update,
    setEntry,
    setBasics,
    setContent,
    setCompensation,
    setEligibility,
    setWorkforce,
    setQuestions,
    setDistribution,
    setConfidential,

    // steps
    step,
    flowSteps,
    progressIndex,
    goToStep,
    goNext,
    stepBack,

    // entry actions
    generateFromBrief,
    startManually,
    isGenerating,

    // validation
    errors,
    errorFor,
    focusFieldId,
    clearFocusField,

    // submit
    submit,
    isSubmitting,
    submittingAs,
  };
}

export type PostJobFlowController = ReturnType<typeof usePostJobFlow>;
